import { Rule } from 'antd/es/form'
import { IntlShape } from 'react-intl'
import dayjs, { Dayjs } from 'dayjs'
import { isValidPhoneNumber } from 'react-phone-number-input'

export const integerRule: Rule = {
  type: 'integer',
  message: 'The input is not valid number',
}

export const minLengthRule = function (length: number, intl: IntlShape): Rule {
  return {
    pattern: new RegExp(`.{${length.toString()},}`, 'g'),
    message: intl.formatMessage(
      { id: 'FIELD.ERROR.MIN_LENGTH' },
      { length: length }
    ),
  }
}

export const capsRule = function (intl: IntlShape): Rule {
  return {
    pattern: new RegExp(/[A-Z]+/g),
    message: intl.formatMessage({ id: 'FIELD.ERROR.CAPS' }),
  }
}
export const lowercaseRule = function (intl: IntlShape): Rule {
  return {
    pattern: new RegExp(/[a-z]+/g),
    message: intl.formatMessage({ id: 'FIELD.ERROR.LOWERCASE' }),
  }
}

export const containsNumberRule = function (intl: IntlShape): Rule {
  return {
    pattern: new RegExp(/\d+/g),
    message: intl.formatMessage({ id: 'FIELD.ERROR.CONTAINS_NUMBER' }),
  }
}
export const containsSymbolRule = function (intl: IntlShape): Rule {
  return {
    pattern: new RegExp(/\W+/g),
    message: intl.formatMessage({ id: 'FIELD.ERROR.CONTAINS_SYMBOL' }),
  }
}

export const numberRule = function (intl: IntlShape): Rule {
  return {
    type: 'number',
    message: intl.formatMessage({ id: 'FIELD.ERROR.TYPE_NUMBER' }),
  }
}

export const stringRule = function (intl: IntlShape): Rule {
  return {
    type: 'string',
    message: intl.formatMessage({ id: 'FIELD.ERROR.TYPE_STRING' }),
  }
}

export const urlRule = function (intl: IntlShape): Rule {
  return {
    type: 'url',
    message: intl.formatMessage({ id: 'FIELD.ERROR.TYPE_URL' }),
  }
}

export const emailRule = function (intl: IntlShape): Rule {
  return {
    type: 'email',
    message: intl.formatMessage({ id: 'FIELD.ERROR.TYPE_EMAIL' }),
  }
}

export const requiredRule = function (intl: IntlShape): Rule {
  return {
    required: true,
    message: intl.formatMessage({ id: 'FIELD.ERROR.REQUIRE' }),
  }
}

export const acceptedRule = function (intl: IntlShape): Rule {
  return {
    validator(_, value) {
      if (value) {
        return Promise.resolve()
      }
      return Promise.reject(intl.formatMessage({ id: 'FIELD.ERROR.REQUIRE' }))
    },
  }
}

export const nestedCascaderExceptAddressRule = function (
  intl: IntlShape,
  minDepth: number = 2
): Rule {
  return {
    validator(_, value) {
      if (value && value.length === 1 && value[0] === 'customaddress') {
        return Promise.resolve()
      }
      return value.length >= minDepth
        ? Promise.resolve()
        : Promise.reject(intl.formatMessage({ id: 'FIELD.ERROR.REQUIRE' }))
    },
  }
}

export const validPhoneNumberRule = function (intl: IntlShape): Rule {
  return ({ getFieldValue }) => ({
    validator(_, value: any) {
      if (!value) {
        // In case there is no value we return success
        // This rule only checks if the input is valid
        return Promise.resolve()
      }
      return isValidPhoneNumber(value)
        ? Promise.resolve()
        : Promise.reject(
            new Error(intl.formatMessage({ id: 'FIELD.ERROR.NOT_VALID_PHONE' }))
          )
    },
  })
}

export const haveMajorityRule = function (intl: IntlShape): Rule {
  return ({ getFieldValue }) => ({
    validator(_, value: boolean) {
      return !value
        ? Promise.reject(
            new Error(intl.formatMessage({ id: 'FIELD.ERROR.NOT_MAJOR' }))
          )
        : Promise.resolve()
    },
  })
}

export const dateMajorRule = function (intl: IntlShape): Rule {
  return ({ getFieldValue }) => ({
    validator(_, value: Dayjs | null) {
      if (!value) {
        return Promise.reject(
          new Error(intl.formatMessage({ id: 'FIELD.ERROR.NOT_MAJOR' }))
        )
      }

      // This is the date when the user has the minimum accepted age
      const majorityDate = value.add(18, 'years')

      return majorityDate < dayjs()
        ? Promise.resolve()
        : Promise.reject(
            new Error(intl.formatMessage({ id: 'FIELD.ERROR.NOT_MAJOR' }))
          )
    },
  })
}

export const matchValueRule = (
  intl: IntlShape,
  expectedValue: string
): Rule => ({
  validator(_rule: any, value: string) {
    if (value === expectedValue) {
      return Promise.resolve()
    }
    return Promise.reject(
      new Error(
        intl.formatMessage({
          id: 'La valeur ne correspond pas à la valeur attendue',
        })
      )
    )
  },
})
